import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, AuthPage, ProtectedRoute, useAuth } from "@/components/Auth";
import Dashboard from "@/components/Dashboard";
import ServiceRequests from "@/components/ServiceRequests";
import AdminProviders from "@/components/AdminProviders";
import DispatcherPanel from "@/components/DispatcherPanel";

// Bottom Navigation Component
function BottomNav() {
  const { user } = useAuth();
  const location = useLocation();
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <nav className="bottom-nav">
      <Link to="/" className={`nav-item ${isActive('/') ? 'active' : ''}`}>
        <span className="nav-icon">🏠</span>
        <span className="nav-label">Home</span>
      </Link>
      
      {user && (
        <Link to="/requests" className={`nav-item ${isActive('/requests') ? 'active' : ''}`}>
          <span className="nav-icon">📋</span>
          <span className="nav-label">Requests</span>
        </Link>
      )}
      
      {user?.role === 'admin' && (
        <Link to="/dispatcher" className={`nav-item ${isActive('/dispatcher') ? 'active' : ''}`}>
          <span className="nav-icon">📡</span>
          <span className="nav-label">Dispatch</span>
        </Link>
      )}
      
      {user ? (
        <Link to="/profile" className={`nav-item ${isActive('/profile') ? 'active' : ''}`}>
          <span className="nav-icon">👤</span>
          <span className="nav-label">Profile</span>
        </Link>
      ) : (
        <Link to="/auth" className={`nav-item ${isActive('/auth') ? 'active' : ''}`}>
          <span className="nav-icon">🔐</span>
          <span className="nav-label">Sign In</span>
        </Link>
      )}
    </nav>
  );
}

// Profile Screen
function ProfileScreen() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  return (
    <div className="requests-screen">
      <div className="requests-header">
        <h1 className="requests-title">Profile</h1>
        <p className="requests-subtitle">Manage your account</p>
      </div>
      
      <div className="payment-card">
        <div className="payment-row">
          <div>
            <div className="payment-label">Name</div>
            <div className="payment-value">{user?.full_name}</div>
          </div>
        </div>
        <div className="payment-row">
          <div>
            <div className="payment-label">Email</div>
            <div className="payment-value">{user?.email}</div>
          </div>
        </div>
        <div className="payment-row">
          <div>
            <div className="payment-label">Role</div>
            <div className="payment-value" style={{ textTransform: 'capitalize' }}>{user?.role}</div>
          </div>
        </div>
        {user?.phone && (
          <div className="payment-row">
            <div>
              <div className="payment-label">Phone</div>
              <div className="payment-value">{user.phone}</div>
            </div>
          </div>
        )}
      </div>
      
      {user?.role === 'admin' && (
        <div style={{ marginBottom: '16px' }}>
          <Link to="/admin/providers" className="cta-button" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: '12px' }}>
            Provider Approvals
          </Link>
        </div>
      )}
      
      <button 
        className="cta-button" 
        onClick={handleLogout}
        style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', boxShadow: 'none' }}
      >
        Sign Out
      </button>
    </div>
  );
}

// Mobile Layout Wrapper
function MobileLayout({ children, showNav = true }) {
  return (
    <div className="app">
      {children}
      {showNav && <BottomNav />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Auth - no bottom nav */}
          <Route 
            path="/auth" 
            element={
              <MobileLayout showNav={false}>
                <AuthPage />
              </MobileLayout>
            } 
          />
          
          {/* Home */}
          <Route
            path="/"
            element={
              <MobileLayout>
                <Dashboard />
              </MobileLayout>
            }
          />
          
          {/* Service Requests */}
          <Route
            path="/requests"
            element={
              <ProtectedRoute>
                <MobileLayout>
                  <ServiceRequests />
                </MobileLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Profile */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <MobileLayout>
                  <ProfileScreen />
                </MobileLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Admin Routes */}
          <Route
            path="/admin/providers"
            element={
              <ProtectedRoute>
                <MobileLayout>
                  <AdminProviders />
                </MobileLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dispatcher"
            element={
              <ProtectedRoute>
                <MobileLayout>
                  <DispatcherPanel />
                </MobileLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
